// Login.jsx

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Context
import { useNavigation } from 'context/NavigationContext';
import { useProgress } from 'context/ProgressContext';
import { useUI } from 'context/UIContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Hooks
import { useIsMobile } from 'hooks/useDeviceDetect';
import useLoginAuthActions from 'login/hooks/useLoginAuthActions';
import useForgotPasswordFormState from 'login/hooks/useForgotPasswordFormState';
import useLoginFormState from 'login/hooks/useLoginFormState';
import useLoginModalState from 'login/hooks/useLoginModalState';
import useSignupFormState from 'login/hooks/useSignupFormState';

// CSS
import './Login.css';

// Components
import LoginError from './error/LoginError';
import LogoLarge from 'components/logo/large/LogoLarge';
import LogoSmall from 'components/logo/small/LogoSmall';
import Modal from 'components/modal/Modal';
import Wizard from 'components/wizard/Wizard';
import LoginStepForgotPassword from 'login/components/LoginStepForgotPassword';
import LoginStepSignIn from 'login/components/LoginStepSignIn';
import LoginStepSignup from 'login/components/LoginStepSignup';

/**
 * Login
 *
 * Renders the auth modal for signing in or creating an account.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} setOpen - Function to open or close the modal.
 * @param {number} [initialStep=1] - Initial wizard step when the modal opens.
 *
 * @returns {ReactNode} The rendered component.
 */
const Login = ({ isOpen, setOpen, initialStep = 1 }) => {
  const { setNewUserProfileSetup } = useUI();
  const { hideProgress, showProgress } = useProgress();
  const { setCurrentSection, postAuthRedirect, setPostAuthRedirect } = useNavigation();
  const { theme } = useTheme();
  const { setUser, setUserSettings } = useUser();
  const { setActiveWorkspace } = useWorkspace();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const {
    modalUiState,
    modalUiHandlers,
  } = useLoginModalState({
    initialStep,
  });

  const { resetForOpen } = modalUiHandlers;

  const {
    loginEmail,
    loginPassword,
    loginFormValid,
    handleLoginEmailChange,
    handleLoginPasswordChange,
    resetLoginForm,
  } = useLoginFormState({
    onErrorClear: () => modalUiState.setErrorMessage(null),
  });

  const {
    forgotEmail,
    forgotFormValid,
    forgotSent,
    handleForgotEmailChange,
    resetForgotForm,
    markForgotSent,
    seedForgotEmail,
  } = useForgotPasswordFormState({
    onErrorClear: () => modalUiState.setErrorMessage(null),
  });

  const {
    signupEmail,
    signupName,
    signupPassword,
    signupFormValid,
    handleSignupEmailChange,
    handleSignupEmailBlur,
    handleSignupNameChange,
    handleSignupPasswordChange,
    resetSignupForm,
  } = useSignupFormState({
    onErrorChange: modalUiState.setErrorMessage,
  });

  useEffect(() => {
    if (!isOpen) return;

    resetForOpen();
    resetLoginForm();
    resetSignupForm();
    resetForgotForm();
  }, [isOpen, resetForOpen, resetLoginForm, resetSignupForm, resetForgotForm]);

  const handleForgotPasswordStepClick = () => {
    seedForgotEmail(loginEmail);
    modalUiHandlers.handleForgotPasswordStepClick();
  };

  const {
    handleLogin,
    handleSignup,
    handleSendPasswordReset,
    handleGoogleSignup,
  } = useLoginAuthActions({
    loginFormValid,
    loginEmail,
    loginPassword,
    forgotFormValid,
    forgotEmail,
    markForgotSent,
    signupFormValid,
    signupEmail,
    signupName,
    signupPassword,
    postAuthRedirect,
    setPostAuthRedirect,
    setCurrentSection,
    showProgress,
    hideProgress,
    navigate,
    setErrorMessage: modalUiState.setErrorMessage,
    setUser,
    setUserSettings,
    setActiveWorkspace,
    setNewUserProfileSetup,
    setOpen,
  });

  const passwordRequirements = [
    'at least 8 characters long',
    'contain an uppercase letter',
    'contain a lowercase letter',
    'contain a number',
    'contain a special character (!@#$%^&*)',
  ];

  const modalWidth = 460;
  const modalHeight = 800;

  return (
    <Modal
      title=""
      isOpen={isOpen}
      onClose={() => setOpen(false)}
      borderRadius={isMobile ? 0 : 24}
      width={isMobile ? '100%' : modalWidth}
      height={isMobile ? '100%' : modalHeight}
      enableClose={false}
      showHeader={false}
    >
      <div className="login-container">
        <div className="login-logo-wrapper">
        <LogoSmall 
          width="250px"
        />
        </div>

        <div className="login-redirect-text"
        style={{
          color: theme.foreground,
        }}
        >
          Orbital is now live at <a href="https://orbital.live" target="_blank" rel="noopener noreferrer">orbital.live</a>.
          <br />
          <br />
          Please use your existing email and password to sign in. 
          <br />
          <br />
          Please update any bookmarks you have to point to the new website.
          <br />
          <br />
          If you need help, please contact support at <a href="mailto:kevin@orbital.com" target="_blank" rel="noopener noreferrer">kevin@orbital.com</a>.
          <br />
          <br />
          Thank you for your patience.
        </div>

        {/* 
        {modalUiState.errorMessage && (
          <div className="login-error-wrapper">
            <LoginError text={modalUiState.errorMessage} />
          </div>
        )}

        <div className="login-wizard-wrapper">
          <Wizard height="auto" step={modalUiState.step} direction={modalUiState.direction}>
            <LoginStepSignIn
              loginEmail={loginEmail}
              loginPassword={loginPassword}
              loginFormValid={loginFormValid}
              handleLoginEmailChange={handleLoginEmailChange}
              handleLoginPasswordChange={handleLoginPasswordChange}
              handleLogin={handleLogin}
              handleSignupEmailStepClick={modalUiHandlers.handleSignupEmailStepClick}
              handleGoogleSignup={handleGoogleSignup}
              handleForgotPasswordStepClick={handleForgotPasswordStepClick}
            />

            <LoginStepSignup
              theme={theme}
              signupEmailInputRef={modalUiState.signupEmailInputRef}
              signupEmail={signupEmail}
              signupName={signupName}
              signupPassword={signupPassword}
              signupFormValid={signupFormValid}
              passwordRequirements={passwordRequirements}
              handleSignupEmailChange={handleSignupEmailChange}
              handleSignupEmailBlur={handleSignupEmailBlur}
              handleSignupNameChange={handleSignupNameChange}
              handleSignupPasswordChange={handleSignupPasswordChange}
              handleSignup={handleSignup}
            />

            <LoginStepForgotPassword
              forgotEmail={forgotEmail}
              forgotFormValid={forgotFormValid}
              forgotSent={forgotSent}
              handleForgotEmailChange={handleForgotEmailChange}
              handleSendPasswordReset={handleSendPasswordReset}
            />
          </Wizard>
        </div>

        {(modalUiState.isSignupStep || modalUiState.isForgotStep) && (
          <div className="login-buttons-wrapper">
            <div
              className="login-signup-link"
              onClick={modalUiState.isForgotStep
                ? modalUiHandlers.handleForgotPasswordBack
                : modalUiHandlers.prevStep}
            >
              Back
            </div>
          </div>
        )}
        */}

      </div>
    </Modal>
  );
};

export default Login;
